import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Alert, Anchor, Button, Stack, TextInput } from "@mantine/core";
import { useAuth } from "../../auth/AuthContext";
import { errorMessage } from "../../api/errors";
import { AuthLayout } from "./AuthLayout";

/**
 * Voluntary password change — the current password is always required.
 *
 * On success the backend re-mints a fresh token; the context swaps it and we
 * navigate into the console.
 */
export function ChangePasswordPage() {
  const { token, changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Not signed in → nothing to change.
  if (!token) return <Navigate to="/login" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirm) {
      setError("The new passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await changePassword({ currentPassword, newPassword });
      navigate("/", { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title="Change password"
      subtitle="Update the password for your account."
      footer={
        <Anchor
          component="button"
          type="button"
          fz="sm"
          onClick={() => {
            logout();
            navigate("/login", { replace: true });
          }}
        >
          Sign out
        </Anchor>
      }
    >
      <form onSubmit={onSubmit} noValidate>
        <Stack gap="md">
          {error && (
            <Alert color="red" variant="light" role="alert">
              {error}
            </Alert>
          )}
          <TextInput
            label="Current password"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.currentTarget.value)}
            placeholder="••••••••"
            required
            autoFocus
          />
          <TextInput
            label="New password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.currentTarget.value)}
            placeholder="••••••••"
            minLength={8}
            required
            // No visual asterisk: keeps the label's text content exactly
            // "New password" so the anchored `/^new password$/i` label query
            // matches (Testing Library's getByLabelText reads label textContent,
            // which would otherwise include Mantine's " *").
            withAsterisk={false}
          />
          <TextInput
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.currentTarget.value)}
            placeholder="••••••••"
            minLength={8}
            required
          />
          <Button
            type="submit"
            size="md"
            fullWidth
            loading={submitting}
            disabled={!newPassword || !confirm}
          >
            Change password
          </Button>
        </Stack>
      </form>
    </AuthLayout>
  );
}

export default ChangePasswordPage;
